import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../components/Layout';
import EntryPassModal from '../components/EntryPassModal';
import PhotoModal from '../components/PhotoModal';
import CallsTab from '../components/ProjectDetailTabs/CallsTab';
import MessagingTab from '../components/ProjectDetailTabs/MessagingTab';
import BrandingTab from '../components/ProjectDetailTabs/BrandingTab';
import PhotosTab from '../components/ProjectDetailTabs/PhotosTab';
import MuralAdmin from '../components/MuralAdmin';
import CustomFormsTab from '../components/ProjectDetailTabs/CustomFormsTab';
import DocumentEditorTab from '../components/ProjectDetailTabs/DocumentEditorTab';
import { projectsAPI, attendeesAPI, brandingAPI, voicesAPI, messagingAPI, callsAPI, photoboothAPI } from '../services/api';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.slice(1);
    const validTabs = ['info', 'fields', 'branding', 'voices', 'messaging', 'calls', 'attendees', 'urls', 'photos', 'mural', 'forms', 'documents'];
    return validTabs.includes(hash) ? hash : 'info';
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const messageTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const [showEntryPass, setShowEntryPass] = useState(false);
    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrModalData, setQrModalData] = useState({ title: '', url: '' });

  const handleSetActiveTab = useCallback((tab) => {
    setActiveTab(tab);
    window.location.hash = tab;
  }, []);

  // States para fields
  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    isRequired: false,
    options: ''
  });

  // States para branding
  const [branding, setBranding] = useState(null);
  const [brandingForm, setBrandingForm] = useState({
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937',
    headerImageUrl: null,
    footerImageUrl: null
  });

  // States para voices
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [voiceForm, setVoiceForm] = useState({
    voiceId: '',
    voiceName: '',
    welcomeMessage: '',
    isActive: true
  });

  // States para messaging
  const [messagingConfig, setMessagingConfig] = useState(null);
  const [messagingForm, setMessagingForm] = useState({
    welcomeMessage: '',
    welcomeMessageEnabled: false,
    reminderMessage: '',
    reminderMessageEnabled: false,
    sendViaSms: true,
    sendViaWhatsapp: false,
    phoneFieldName: 'phone',
    // Campos de email
    emailSubject: 'Tu acceso para {project}',
    emailMessage: 'Hola {name}, adjuntamos tu pase de acceso para {project}. Haz clic en el siguiente enlace para ver tu código QR: {qr_link}',
    emailEnabled: true,
    sendEmailWithLink: true,
    // Campos de diseño del email
    emailHeaderImage: null,
    emailBackgroundColor: '#F3F4F6',
    emailUseCustomDesign: false,
    // Campos de firma del email
    emailSignatureType: 'none',
    emailSignatureText: '',
    emailSignatureImage: null
  });
  const [emailHeaderFile, setEmailHeaderFile] = useState(null);
  const [uploadingEmailHeader, setUploadingEmailHeader] = useState(false);
  const [emailSignatureFile, setEmailSignatureFile] = useState(null);
  const [uploadingEmailSignature, setUploadingEmailSignature] = useState(false);

  // States para calls (Retell AI + Twilio)
  const [callsConfig, setCallsConfig] = useState(null);
  const [globalTwilioNumber, setGlobalTwilioNumber] = useState(null);
  const [callsForm, setCallsForm] = useState({
    agentName: 'Asistente Virtual',
    agentVoice: 'es-ES-Standard-A',
    agentLanguage: 'es',
    systemPrompt: '',
    firstMessage: 'Hola {name}, te llamo de {project}. ¿Tienes un momento?',
    callEnabled: false,
    maxCallDuration: 300,
    useAttendeeName: true,
    useProjectName: true,
    useCustomPhone: false,
    twilioPhoneNumber: ''
  });
  const [callHistory, setCallHistory] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [availableCallVoices, setAvailableCallVoices] = useState([]);
  const [voiceSearchTerm, setVoiceSearchTerm] = useState('');
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  // PhotoBooth states
  const [photoBoothPhotos, setPhotoBoothPhotos] = useState([]);
  const [bannedPhotos, setBannedPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photoboothToken, setPhotoboothToken] = useState('');

  // Helper function to construct proper image URLs
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it already has the correct path, return as is
    if (imagePath.startsWith('/uploads/branding/')) {
      return imagePath;
    }
    // Just the filename, need to prepend the path
    return `/uploads/branding/${imagePath}`;
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      const validTabs = ['info', 'fields', 'branding', 'voices', 'messaging', 'calls', 'attendees', 'urls', 'photos', 'mural', 'forms', 'documents'];
      if (validTabs.includes(hash)) {
        setActiveTab(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const validTabs = ['info', 'fields', 'branding', 'voices', 'messaging', 'calls', 'attendees', 'urls', 'photos', 'mural', 'forms', 'documents'];
    if (validTabs.includes(hash)) {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    loadProject();
    loadAttendees();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'voices') {
      loadAvailableVoices();
      loadProjectVoice();
    } else if (activeTab === 'branding') {
      loadBranding();
    } else if (activeTab === 'messaging') {
      loadMessagingConfig();
    } else if (activeTab === 'calls') {
      loadCallsConfig();
      loadGlobalTwilioNumber();
      loadAvailableCallVoices();
      loadCallHistory();
    } else if (activeTab === 'photos' && project?.slug) {
      loadPhotoBoothPhotos();
      loadBannedPhotos();
    }
  }, [activeTab, project?.slug]);

  const loadProject = async () => {
    try {
      const response = await projectsAPI.getById(id);
      if (!isMountedRef.current) return;
      setProject(response.data.project);
      if (response.data.project.fields) {
        setFields(response.data.project.fields);
      }
      // Cargar token de photobooth
      if (response.data.project.slug) {
        loadPhotoBoothToken(response.data.project.slug);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error:', error);
      showMessage('error', 'Error cargando proyecto');
    }
  };

  const loadPhotoBoothToken = async (slug) => {
    try {
      const response = await photoboothAPI.getInfo(slug);
      if (!isMountedRef.current) return;
      if (response.data.success && response.data.info && response.data.info.currentToken) {
        setPhotoboothToken(response.data.info.currentToken);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error cargando token de photobooth:', error);
    }
  };

  const loadAttendees = async () => {
    try {
      const response = await attendeesAPI.getProjectAttendees(id);
      if (!isMountedRef.current) return;
      setAttendees(response.data.attendees);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error:', error);
    }
  };

  const loadBranding = async () => {
    try {
      const response = await brandingAPI.get(id);
      if (response.data.branding) {
        setBranding(response.data.branding);
        setBrandingForm({
          primaryColor: response.data.branding.primary_color || '#3B82F6',
          secondaryColor: response.data.branding.secondary_color || '#10B981',
          backgroundColor: response.data.branding.background_color || '#FFFFFF',
          textColor: response.data.branding.text_color || '#1F2937',
          headerImageUrl: response.data.branding.header_image || null,
          footerImageUrl: response.data.branding.footer_image || null
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadAvailableVoices = async () => {
    try {
      const response = await voicesAPI.getAvailable();
      setAvailableVoices(response.data.voices || []);

      // Mostrar warning si ElevenLabs no está configurado
      if (response.data.warning && response.data.voices.length === 0) {
        console.warn('⚠️  [VOICES]', response.data.warning);
        showMessage('warning', 'ElevenLabs no está configurado. Configura ELEVENLABS_API_KEY para habilitar mensajes de voz.');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('error', 'Error cargando voces disponibles');
    }
  };

  const loadProjectVoice = async () => {
    try {
      const response = await voicesAPI.getProjectVoice(id);
      if (response.data.voice) {
        setVoiceConfig(response.data.voice);
        setVoiceForm({
          voiceId: response.data.voice.voice_id,
          voiceName: response.data.voice.voice_name,
          welcomeMessage: response.data.voice.welcome_message,
          isActive: response.data.voice.is_active
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadMessagingConfig = async () => {
    try {
      const response = await messagingAPI.getConfig(id);
      if (response.data.config) {
        setMessagingConfig(response.data.config);
        setMessagingForm({
          welcomeMessage: response.data.config.welcome_message || '',
          welcomeMessageEnabled: response.data.config.welcome_message_enabled || false,
          reminderMessage: response.data.config.reminder_message || '',
          reminderMessageEnabled: response.data.config.reminder_message_enabled || false,
          sendViaSms: response.data.config.send_via_sms !== undefined ? response.data.config.send_via_sms : true,
          sendViaWhatsapp: response.data.config.send_via_whatsapp || false,
          phoneFieldName: response.data.config.phone_field_name || 'phone',
          // Campos de email
          emailSubject: response.data.config.email_subject || 'Tu acceso para {project}',
          emailMessage: response.data.config.email_message || 'Hola {name}, adjuntamos tu pase de acceso para {project}. Haz clic en el siguiente enlace para ver tu código QR: {qr_link}',
          emailEnabled: response.data.config.email_enabled !== undefined ? response.data.config.email_enabled : true,
          sendEmailWithLink: response.data.config.send_email_with_link !== undefined ? response.data.config.send_email_with_link : true,
          // Campos de diseño del email
          emailHeaderImage: response.data.config.email_header_image || null,
          emailBackgroundColor: response.data.config.email_background_color || '#F3F4F6',
          emailUseCustomDesign: response.data.config.email_use_custom_design || false,
          // Campos de firma del email
          emailSignatureType: response.data.config.email_signature_type || 'none',
          emailSignatureText: response.data.config.email_signature_text || '',
          emailSignatureImage: response.data.config.email_signature_image || null
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Handlers para Calls
  const loadCallsConfig = async () => {
    try {
      const response = await callsAPI.getConfig(id);
      if (response.data.config) {
        setCallsConfig(response.data.config);
        setCallsForm({
          agentName: response.data.config.agent_name || 'Asistente Virtual',
          agentVoice: response.data.config.agent_voice || 'es-ES-Standard-A',
          agentLanguage: response.data.config.agent_language || 'es',
          systemPrompt: response.data.config.system_prompt || '',
          firstMessage: response.data.config.first_message || 'Hola {name}, te llamo de {project}. ¿Tienes un momento?',
          callEnabled: response.data.config.call_enabled || false,
          maxCallDuration: response.data.config.max_call_duration || 300,
          useAttendeeName: response.data.config.use_attendee_name !== undefined ? response.data.config.use_attendee_name : true,
          useProjectName: response.data.config.use_project_name !== undefined ? response.data.config.use_project_name : true,
          useCustomPhone: !!response.data.config.twilio_phone_number,
          twilioPhoneNumber: response.data.config.twilio_phone_number || ''
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadGlobalTwilioNumber = async () => {
    try {
      const response = await callsAPI.getGlobalNumber();
      setGlobalTwilioNumber(response.data.global_number);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadAvailableCallVoices = async () => {
    try {
      const response = await callsAPI.getAvailableVoices();
      if (response.data.success) {
        setAvailableCallVoices(response.data.voices || []);
        console.log(`✅ ${response.data.voices.length} voces disponibles cargadas`);
      }
    } catch (error) {
      console.error('Error cargando voces:', error);
      showMessage('warning', 'No se pudieron cargar las voces disponibles');
    }
  };

  const loadCallHistory = async () => {
    try {
      const response = await callsAPI.getHistory(id, { limit: 50 });
      setCallHistory(response.data.calls || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // PhotoBooth functions
  const loadPhotoBoothPhotos = async () => {
    if (!project?.slug) return;
    setLoadingPhotos(true);
    try {
      const response = await photoboothAPI.getPhotos(project.slug, { limit: 100 });
      if (response.data.success) {
        setPhotoBoothPhotos(response.data.photos || []);
      }
    } catch (error) {
      console.error('Error cargando fotos:', error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const loadBannedPhotos = async () => {
    if (!project?.slug) return;
    try {
      const response = await photoboothAPI.getBannedPhotos(project.slug);
      if (response.data.success) {
        setBannedPhotos(response.data.bannedPhotos || []);
      }
    } catch (error) {
      console.error('Error cargando fotos baneadas:', error);
    }
  };

  const handleBanPhoto = async (fileName) => {
    if (!window.confirm('¿Banear esta foto?\n\nLa foto será ocultada de la presentación.')) {
      return;
    }
    try {
      await photoboothAPI.deletePhoto(project.slug, fileName);
      showMessage('success', 'Foto baneada correctamente');
      await loadPhotoBoothPhotos();
      await loadBannedPhotos();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al banear foto');
    }
  };

  const handleDeleteBoothPhoto = async (fileName) => {
    try {
      await photoboothAPI.deletePhotoPermanently(project.slug, fileName);
      showMessage('success', 'Foto eliminada correctamente');
      await loadPhotoBoothPhotos();
      await loadBannedPhotos();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al eliminar foto');
    }
  };

  const handleUnbanPhoto = async (fileName) => {
    if (!window.confirm('¿Desbanear esta foto?\n\nLa foto volverá a aparecer en la presentación.')) {
      return;
    }
    try {
      await photoboothAPI.unbanPhoto(project.slug, fileName);
      showMessage('success', 'Foto desbaneada correctamente');
      await loadPhotoBoothPhotos();
      await loadBannedPhotos();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error al desbanear foto');
    }
  };

  const handleSaveCalls = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const configData = {
        agentName: callsForm.agentName,
        agentVoice: callsForm.agentVoice,
        agentLanguage: callsForm.agentLanguage,
        systemPrompt: callsForm.systemPrompt,
        firstMessage: callsForm.firstMessage,
        callEnabled: callsForm.callEnabled,
        maxCallDuration: parseInt(callsForm.maxCallDuration),
        useAttendeeName: callsForm.useAttendeeName,
        useProjectName: callsForm.useProjectName,
        twilioPhoneNumber: callsForm.useCustomPhone ? callsForm.twilioPhoneNumber : null
      };

      await callsAPI.updateConfig(id, configData);
      showMessage('success', 'Configuración de llamadas guardada exitosamente');
      await loadCallsConfig();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error guardando configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCallCampaign = async () => {
    if (!callsForm.callEnabled) {
      showMessage('error', 'Las llamadas deben estar habilitadas primero');
      return;
    }

    if (!window.confirm(`¿Iniciar campaña de llamadas a todos los asistentes (${attendees.length})?\n\nEsto iniciará llamadas telefónicas automáticas.`)) {
      return;
    }

    setLoadingCalls(true);
    try {
      const response = await callsAPI.startCampaign(id);
      showMessage('success', `Campaña iniciada: ${response.data.initiated} llamadas`);
      await loadCallHistory();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error iniciando campaña');
    } finally {
      setLoadingCalls(false);
    }
  };

  const handleConfigureTwilioWebhook = async () => {
    if (!window.confirm('¿Configurar el número de Twilio para Retell AI?\n\nEsto actualizará los webhooks de voz en Twilio para que apunten a Retell AI.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await callsAPI.configureTwilioWebhook();

      if (response.data.success) {
        const config = response.data.configuration;
        showMessage('success', '✅ Webhook de Twilio configurado exitosamente');
        console.log('Configuración:', config);

        // Verificar si hay advertencias
        const hasTrunkWarning = config.trunkSid !== null && config.trunkSid !== undefined;

        // Mostrar detalles en un alert
        let message = `✅ Configuración ${hasTrunkWarning ? 'Parcial' : 'Exitosa'}\n\n` +
          `Número: ${config.phoneNumber}\n` +
          `Voice URL: ${config.voiceUrl}\n` +
          `Voice Mode: ${config.voiceReceiveMode || 'voice'}\n` +
          `Agente: ${config.retellAgent}\n`;

        if (hasTrunkWarning) {
          message += `\n⚠️ ADVERTENCIA: SIP Trunk sigue activo (${config.trunkSid})\n\n` +
            `Esto puede causar que las llamadas no funcionen.\n` +
            `Por favor ve manualmente a Twilio Console:\n` +
            `1. Phone Numbers → Active Numbers\n` +
            `2. Haz clic en ${config.phoneNumber}\n` +
            `3. En "Voice Configuration" → "Configure with" selecciona "Webhooks"\n` +
            `4. En "A call comes in" debería estar: ${config.voiceUrl}\n` +
            `5. Guarda los cambios`;
        } else {
          message += `\n✅ SIP Trunk desactivado correctamente\n` +
            `Ahora puedes hacer llamadas. El teléfono debería sonar.`;
        }

        alert(message);
      }
    } catch (error) {
      console.error('Error configurando webhook:', error);

      if (error.response?.data?.error === 'El número no está registrado en Retell AI') {
        const instructions = error.response.data.instructions || [];
        showMessage('error', 'El número no está registrado en Retell AI');

        alert(`❌ Número no registrado en Retell AI\n\n` +
          `Por favor sigue estos pasos:\n\n` +
          instructions.join('\n')
        );
      } else {
        showMessage('error', error.response?.data?.error || 'Error configurando webhook de Twilio');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureSIPAuth = async () => {
    if (!window.confirm(
      '🔐 Configurar Autenticación SIP\n\n' +
      'Esto es CRÍTICO para que funcionen las llamadas salientes.\n\n' +
      'Este proceso:\n' +
      '1. Configurará credenciales seguras en Twilio SIP Trunk\n' +
      '2. Registrará las credenciales en Retell AI\n' +
      '3. Asociará tu número al SIP Trunk\n\n' +
      '¿Continuar?'
    )) {
      return;
    }

    setLoading(true);
    try {
      const response = await callsAPI.configureSIPAuth();

      if (response.data.success) {
        const config = response.data.configuration;

        // Mostrar resumen de configuración
        let message = `🎉 ${response.data.message}\n\n`;
        message += `📱 Número: ${config.phoneNumber}\n`;
        message += `📞 SIP Trunk: ${config.twilioTrunkName}\n`;
        message += `🔐 Usuario SIP: ${config.sipUsername}\n`;
        message += `🔑 Contraseña SIP: ${config.sipPassword}\n`;
        message += `🌐 Termination URI: ${config.terminationUri || 'Configurando...'}\n`;
        message += `🤖 Agente Outbound: ${config.outboundAgent || 'NO CONFIGURADO ⚠️'}\n\n`;

        if (!config.outboundAgent) {
          message += `⚠️ ADVERTENCIA: No hay agente outbound configurado\n\n`;
          message += `Por favor ve a Retell AI Dashboard:\n`;
          message += `1. Ve a https://app.retellai.com → Phone Numbers\n`;
          message += `2. Edita el número ${config.phoneNumber}\n`;
          message += `3. Configura un "Outbound Agent"\n`;
          message += `4. Guarda los cambios\n\n`;
        }

        message += `Próximos pasos:\n`;
        config.nextSteps.forEach((step, index) => {
          message += `${index + 1}. ${step}\n`;
        });

        alert(message);
        showMessage('success', '🎉 Autenticación SIP configurada exitosamente');
      }
    } catch (error) {
      console.error('Error configurando SIP:', error);

      if (error.response?.data?.error === 'Número no encontrado en Retell AI') {
        const instructions = error.response.data.instructions || [];
        showMessage('error', 'Número no encontrado en Retell AI');

        alert(`❌ Número no encontrado en Retell AI\n\n` +
          `Números disponibles: ${error.response.data.available?.join(', ') || 'ninguno'}\n\n` +
          `Por favor:\n` +
          instructions.join('\n')
        );
      } else {
        const errorMsg = error.response?.data?.details || error.response?.data?.error || 'Error configurando autenticación SIP';
        showMessage('error', errorMsg);
        alert(`❌ Error configurando SIP\n\n${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunDiagnostic = async () => {
    setLoading(true);
    try {
      const response = await callsAPI.getDiagnostic();

      if (response.data.success) {
        const { diagnostic, summary } = response.data;

        let message = `🔍 DIAGNÓSTICO DE CONFIGURACIÓN\n`;
        message += `═══════════════════════════════\n\n`;

        message += `📊 RESUMEN:\n`;
        message += `✅ Éxitos: ${summary.totalSuccess}\n`;
        message += `⚠️  Advertencias: ${summary.totalWarnings}\n`;
        message += `❌ Errores: ${summary.totalIssues}\n`;
        message += `🎯 ¿Puede hacer llamadas?: ${summary.canMakeCalls ? 'SÍ ✅' : 'NO ❌'}\n\n`;

        if (diagnostic.success.length > 0) {
          message += `✅ CONFIGURACIONES CORRECTAS:\n`;
          diagnostic.success.forEach(s => message += `  • ${s}\n`);
          message += `\n`;
        }

        if (diagnostic.warnings.length > 0) {
          message += `⚠️  ADVERTENCIAS:\n`;
          diagnostic.warnings.forEach(w => message += `  • ${w}\n`);
          message += `\n`;
        }

        if (diagnostic.issues.length > 0) {
          message += `❌ PROBLEMAS ENCONTRADOS:\n`;
          diagnostic.issues.forEach(i => message += `  • ${i}\n`);
          message += `\n`;
        }

        if (diagnostic.recommendations && diagnostic.recommendations.length > 0) {
          message += `🔧 RECOMENDACIONES:\n\n`;
          diagnostic.recommendations.forEach((rec, index) => {
            message += `${index + 1}. [${rec.priority}] ${rec.action}\n`;
            rec.steps.forEach((step, idx) => {
              message += `   ${idx + 1}) ${step}\n`;
            });
            message += `\n`;
          });
        }

        // Log detallado en consola
        console.log('🔍 Diagnóstico Completo:', diagnostic);

        alert(message);

        if (summary.canMakeCalls) {
          showMessage('success', '✅ Configuración completa - puedes hacer llamadas');
        } else {
          showMessage('warning', '⚠️ Configuración incompleta - revisa las recomendaciones');
        }
      }
    } catch (error) {
      console.error('Error en diagnóstico:', error);
      showMessage('error', error.response?.data?.details || 'Error ejecutando diagnóstico');
      alert(`❌ Error ejecutando diagnóstico\n\n${error.response?.data?.details || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    setMessage({ type, text });
    messageTimeoutRef.current = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Cleanup message timeout and isMounted on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Handlers para Fields
  const handleAddField = async (e) => {
    e.preventDefault();
    if (!newField.fieldName || !newField.fieldLabel) {
      showMessage('error', 'Nombre y etiqueta del campo son requeridos');
      return;
    }

    setLoading(true);
    try {
      const fieldData = {
        fieldName: newField.fieldName,
        fieldLabel: newField.fieldLabel,
        fieldType: newField.fieldType,
        isRequired: newField.isRequired,
        options: newField.options ? newField.options.split(',').map(o => o.trim()) : null
      };

      await projectsAPI.addField(id, fieldData);
      showMessage('success', 'Campo agregado exitosamente');
      setNewField({ fieldName: '', fieldLabel: '', fieldType: 'text', isRequired: false, options: '' });
      await loadProject();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error agregando campo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('¿Estás seguro de eliminar este campo?')) return;

    setLoading(true);
    try {
      await projectsAPI.deleteField(id, fieldId);
      showMessage('success', 'Campo eliminado exitosamente');
      await loadProject();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error eliminando campo');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = async (fieldId, updates) => {
    setLoading(true);
    try {
      await projectsAPI.updateField(id, fieldId, updates);
      showMessage('success', 'Campo actualizado');
      await loadProject();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error actualizando campo');
    } finally {
      setLoading(false);
    }
  };

  // Handlers para Branding
  const handleUpdateBranding = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await brandingAPI.update(id, brandingForm);
      showMessage('success', 'Branding actualizado exitosamente');
      await loadBranding();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error actualizando branding');
    } finally {
      setLoading(false);
    }
  };

  // Handlers para subir imágenes de branding
  const handleUploadHeaderImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      await brandingAPI.uploadHeader(id, file);
      showMessage('success', 'Imagen de encabezado subida exitosamente');
      await loadBranding();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error subiendo imagen');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFooterImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      await brandingAPI.uploadFooter(id, file);
      showMessage('success', 'Imagen de pie de página subida exitosamente');
      await loadBranding();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error subiendo imagen');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHeaderImage = async () => {
    if (!window.confirm('¿Eliminar la imagen de encabezado?')) return;

    setLoading(true);
    try {
      await brandingAPI.deleteHeader(id);
      showMessage('success', 'Imagen de encabezado eliminada');
      await loadBranding();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error eliminando imagen');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFooterImage = async () => {
    if (!window.confirm('¿Eliminar la imagen de pie de página?')) return;

    setLoading(true);
    try {
      await brandingAPI.deleteFooter(id);
      showMessage('success', 'Imagen de pie de página eliminada');
      await loadBranding();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error eliminando imagen');
    } finally {
      setLoading(false);
    }
  };

  // Handlers para Voices
  const handleSaveVoice = async (e) => {
    e.preventDefault();
    if (!voiceForm.voiceId || !voiceForm.welcomeMessage) {
      showMessage('error', 'Voz y mensaje de bienvenida son requeridos');
      return;
    }

    setLoading(true);
    try {
      await voicesAPI.configure(id, voiceForm);
      showMessage('success', 'Configuración de voz guardada');
      await loadProjectVoice();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error guardando configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleTestVoice = async () => {
    console.log('🔍 [FRONTEND] voiceForm:', voiceForm);
    console.log('🔍 [FRONTEND] voiceForm.voiceId:', voiceForm.voiceId);

    if (!voiceForm.voiceId) {
      showMessage('error', 'Selecciona una voz primero');
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 [FRONTEND] Enviando test con voiceId:', voiceForm.voiceId);
      const response = await voicesAPI.test(voiceForm.voiceId, voiceForm.welcomeMessage || 'Mensaje de prueba');

      // Reproducir el audio recibido
      if (response.data.audioData) {
        console.log('🔊 [FRONTEND] Reproduciendo audio...');

        // Convertir base64 a blob
        const audioBlob = base64ToBlob(response.data.audioData, response.data.audioFormat || 'audio/mpeg');

        // Crear URL del blob
        const audioUrl = URL.createObjectURL(audioBlob);

        // Reproducir audio
        const audio = new Audio(audioUrl);
        audio.play()
          .then(() => {
            console.log('✅ [FRONTEND] Audio reproduciéndose');
            showMessage('success', '🔊 Reproduciendo audio de prueba...');
          })
          .catch(err => {
            console.error('❌ [FRONTEND] Error reproduciendo audio:', err);
            showMessage('error', 'Error reproduciendo audio');
          });

        // Limpiar URL después de reproducir
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          console.log('✅ [FRONTEND] Audio completado');
        };
      } else {
        showMessage('success', 'Prueba de voz completada (sin audio)');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error en test:', error);
      console.error('❌ [FRONTEND] Error response:', error.response);
      showMessage('error', error.response?.data?.error || 'Error probando voz');
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para convertir base64 a Blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  // Handlers para Messaging
  const handleEmailHeaderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Por favor selecciona una imagen válida');
        return;
      }
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showMessage('error', 'La imagen no debe superar 5MB');
        return;
      }
      setEmailHeaderFile(file);
    }
  };

  const uploadEmailHeader = async () => {
    if (!emailHeaderFile) return null;

    setUploadingEmailHeader(true);
    try {
      const formData = new FormData();
      formData.append('header', emailHeaderFile);

      const response = await messagingAPI.uploadEmailHeader(id, formData);
      return response.data.imageUrl;
    } catch (error) {
      console.error('Error subiendo imagen:', error);
      throw error;
    } finally {
      setUploadingEmailHeader(false);
    }
  };

  const handleEmailSignatureFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Por favor selecciona una imagen válida');
        return;
      }
      // Validar tamaño (máximo 2MB para firmas)
      if (file.size > 2 * 1024 * 1024) {
        showMessage('error', 'La imagen no debe superar 2MB');
        return;
      }
      setEmailSignatureFile(file);
    }
  };

  const uploadEmailSignature = async () => {
    if (!emailSignatureFile) return null;

    setUploadingEmailSignature(true);
    try {
      const formData = new FormData();
      formData.append('signature', emailSignatureFile);

      const response = await messagingAPI.uploadEmailSignature(id, formData);
      return response.data.imageUrl;
    } catch (error) {
      console.error('Error subiendo imagen de firma:', error);
      throw error;
    } finally {
      setUploadingEmailSignature(false);
    }
  };

  const handleSaveMessaging = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Primero subir las imágenes si hay nuevas
      let updatedForm = { ...messagingForm };

      if (emailHeaderFile) {
        const imageUrl = await uploadEmailHeader();
        updatedForm.emailHeaderImage = imageUrl;
      }

      if (emailSignatureFile) {
        const imageUrl = await uploadEmailSignature();
        updatedForm.emailSignatureImage = imageUrl;
        updatedForm.emailSignatureType = 'image'; // Cambiar tipo automáticamente
      }

      await messagingAPI.updateConfig(id, updatedForm);
      showMessage('success', 'Configuración de mensajes, diseño y firma guardada exitosamente');
      setEmailHeaderFile(null); // Limpiar archivos seleccionados
      setEmailSignatureFile(null);
      await loadMessagingConfig();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error guardando configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!messagingForm.reminderMessageEnabled || !messagingForm.reminderMessage) {
      showMessage('error', 'El mensaje de recordatorio debe estar habilitado y configurado');
      return;
    }

    if (!window.confirm(`¿Enviar mensaje de recordatorio a todos los asistentes (${attendees.length})?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await messagingAPI.sendReminder(id);
      showMessage('success', response.data.message);
      await loadAttendees();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error enviando recordatorio');
    } finally {
      setLoading(false);
    }
  };

  // Handler para limpiar todos los asistentes del proyecto
  const handleClearAllAttendees = async () => {
    const confirmMessage = `⚠️ ATENCIÓN: Esta acción eliminará TODOS los ${attendees.length} asistentes registrados en este proyecto.\n\n` +
      `Esta acción es útil para pruebas antes de lanzar el evento, pero NO se puede deshacer.\n\n` +
      `¿Estás seguro de continuar?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Segunda confirmación
    const doubleConfirm = window.prompt(
      `Para confirmar, escribe el nombre del proyecto: "${project.name}"`
    );

    if (doubleConfirm !== project.name) {
      showMessage('error', 'El nombre del proyecto no coincide. Operación cancelada.');
      return;
    }

    setLoading(true);
    try {
      const response = await attendeesAPI.deleteAllProjectAttendees(id);
      showMessage('success', `✅ ${response.data.message} (${response.data.deletedCount} eliminados)`);
      // Recargar lista de asistentes
      await loadAttendees();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error limpiando asistentes');
    } finally {
      setLoading(false);
    }
  };

  // Handler para eliminar un asistente individual
  const handleDeleteAttendee = async (attendeeId) => {
    const attendee = attendees.find(a => a.id === attendeeId);
    if (!attendee) return;

    if (!window.confirm(`¿Eliminar al asistente "${attendee.fullName}"?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    setLoading(true);
    try {
      await attendeesAPI.delete(attendeeId);
      showMessage('success', `Asistente "${attendee.fullName}" eliminado`);
      await loadAttendees();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error eliminando asistente');
    } finally {
      setLoading(false);
    }
  };

  // Handler para borrar el check-in de un asistente (para pruebas)
  const handleClearCheckIn = async (attendeeId) => {
    const attendee = attendees.find(a => a.id === attendeeId);
    if (!attendee) return;

    if (!window.confirm(`¿Borrar el check-in de ${attendee.fullName}?\n\nEsto permitirá que pueda hacer check-in nuevamente (útil para pruebas).`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await attendeesAPI.clearCheckIn(attendeeId);
      showMessage('success', `✅ ${response.data.message}`);
      // Recargar lista de asistentes
      await loadAttendees();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error borrando check-in');
    } finally {
      setLoading(false);
    }
  };

// Handler para mostrar la ficha de ingreso (QR)
    const handleShowEntryPass = (attendee) => {
      setSelectedAttendee(attendee);
      setShowEntryPass(true);
    };

    // Handler para mostrar foto facial
    const handleShowPhoto = (attendee) => {
      setSelectedAttendee(attendee);
      setShowPhotoModal(true);
    };

    // Handler para mostrar modal QR
    const handleShowQR = (title, url) => {
      setQrModalData({ title, url });
      setShowQRModal(true);
    };

    // Handler para copiar URL
    const handleCopyUrl = (url) => {
      navigator.clipboard.writeText(url);
      showMessage('success', 'URL copiada al portapapeles');
    };

    // Handler para compartir URL
    const handleShareUrl = async (title, url) => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: title,
            text: `Accede a ${title}`,
            url: url
          });
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Error sharing:', err);
          }
        }
      } else {
        handleCopyUrl(url);
      }
    };

    // Handler para eliminar foto facial
    const handleDeletePhoto = async (attendeeId) => {
      const attendee = attendees.find(a => a.id === attendeeId);
      if (!attendee) return;

      if (!window.confirm(`¿Eliminar la foto facial de ${attendee.fullName}?\n\nEsta acción no se puede deshacer.`)) {
        return;
      }

      setLoading(true);
      try {
        await attendeesAPI.deletePhoto(attendeeId);
        showMessage('success', `Foto facial de "${attendee.fullName}" eliminada`);
        await loadAttendees(); // Recargar lista para reflejar el cambio
      } catch (error) {
        showMessage('error', error.response?.data?.error || 'Error eliminando foto facial');
      } finally {
        setLoading(false);
      }
    };

  // Handler para exportar a PDF
  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const response = await attendeesAPI.exportPDF(project.id);

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asistentes-${project.name.replace(/\s+/g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showMessage('success', '✅ PDF descargado exitosamente');
    } catch (error) {
      console.error('Error exportando PDF:', error);
      showMessage('error', error.response?.data?.error || 'Error exportando PDF');
    } finally {
      setLoading(false);
    }
  };

  // Handler para exportar a Excel
  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const response = await attendeesAPI.exportExcel(project.id);

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asistentes-${project.name.replace(/\s+/g, '-')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showMessage('success', '✅ Excel descargado exitosamente');
    } catch (error) {
      console.error('Error exportando Excel:', error);
      showMessage('error', error.response?.data?.error || 'Error exportando Excel');
    } finally {
      setLoading(false);
    }
  };

  if (!project) return <Layout><div>Cargando...</div></Layout>;

  const baseUrl = window.location.origin;

  const fieldTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Teléfono' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Fecha' },
    { value: 'select', label: 'Selección' },
    { value: 'textarea', label: 'Área de texto' },
    { value: 'checkbox', label: 'Casilla de verificación' },
    { value: 'photo', label: 'Foto' },
    { value: 'facial_recognition', label: 'Reconocimiento Facial' }
  ];

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <h1 className="text-3xl font-bold mb-6">{project.name}</h1>

        {message.text && (
          <div className={`mb-4 p-4 rounded flex justify-between items-center ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 
            message.type === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
            'bg-red-100 text-red-800'
          }`}>
            <span>{message.text}</span>
            <button 
              onClick={() => setMessage({ type: '', text: '' })}
              className="ml-4 text-lg font-bold hover:opacity-70 transition-opacity"
            >
              ×
            </button>
          </div>
        )}

        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {['info', 'fields', 'branding', 'voices', 'messaging', 'calls', 'attendees', 'urls', 'photos', 'mural', 'forms', 'documents'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleSetActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'forms' ? 'Formulario' : tab === 'documents' ? 'Documentos' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'info' && (
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Información del Proyecto</h2>
            <p className="mb-2"><strong>Descripción:</strong> {project.description}</p>
            <p className="mb-2"><strong>Slug:</strong> {project.slug}</p>
            <p className="mb-2"><strong>Estado:</strong> {project.is_active ? 'Activo' : 'Inactivo'}</p>
          </div>
        )}

        {activeTab === 'fields' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-bold mb-4">Campos del Formulario</h2>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Campos Actuales ({fields.length})</h3>
                {fields.length === 0 ? (
                  <p className="text-gray-500">No hay campos configurados</p>
                ) : (
                  <div className="space-y-2">
                    {fields.map((field) => (
                      <div key={field.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="font-medium">{field.field_label}</p>
                          <p className="text-sm text-gray-500">
                            Tipo: {field.field_type} | Campo: {field.field_name}
                            {field.is_required && <span className="ml-2 text-red-500">*</span>}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={field.is_active}
                              onChange={(e) => handleUpdateField(field.id, { isActive: e.target.checked })}
                              className="mr-2"
                            />
                            <span className="text-sm">Activo</span>
                          </label>
                          <button
                            onClick={() => handleDeleteField(field.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <hr className="my-6" />

              <h3 className="text-lg font-semibold mb-3">Agregar Nuevo Campo</h3>
              <form onSubmit={handleAddField} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Nombre del Campo (nombre técnico)</label>
                    <input
                      type="text"
                      value={newField.fieldName}
                      onChange={(e) => setNewField({...newField, fieldName: e.target.value})}
                      className="input-field"
                      placeholder="ej: company, phone, etc"
                    />
                  </div>
                  <div>
                    <label className="label">Etiqueta (nombre visible)</label>
                    <input
                      type="text"
                      value={newField.fieldLabel}
                      onChange={(e) => setNewField({...newField, fieldLabel: e.target.value})}
                      className="input-field"
                      placeholder="ej: Empresa, Teléfono, etc"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Tipo de Campo</label>
                    <select
                      value={newField.fieldType}
                      onChange={(e) => setNewField({...newField, fieldType: e.target.value})}
                      className="input-field"
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center mt-8">
                      <input
                        type="checkbox"
                        checked={newField.isRequired}
                        onChange={(e) => setNewField({...newField, isRequired: e.target.checked})}
                        className="mr-2"
                      />
                      Campo requerido
                    </label>
                  </div>
                </div>

                {newField.fieldType === 'select' && (
                  <div>
                    <label className="label">Opciones (separadas por comas)</label>
                    <input
                      type="text"
                      value={newField.options}
                      onChange={(e) => setNewField({...newField, options: e.target.value})}
                      className="input-field"
                      placeholder="Opción 1, Opción 2, Opción 3"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Agregando...' : 'Agregar Campo'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <BrandingTab
            brandingForm={brandingForm}
            setBrandingForm={setBrandingForm}
            loading={loading}
            handleUpdateBranding={handleUpdateBranding}
            handleUploadHeaderImage={handleUploadHeaderImage}
            handleDeleteHeaderImage={handleDeleteHeaderImage}
            handleUploadFooterImage={handleUploadFooterImage}
            handleDeleteFooterImage={handleDeleteFooterImage}
            getImageUrl={getImageUrl}
          />
        )}

        {activeTab === 'voices' && (
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Configuración de Voz</h2>
            <p className="text-gray-600 mb-6">
              Configura mensajes de bienvenida personalizados con voces de ElevenLabs
            </p>

            <form onSubmit={handleSaveVoice} className="space-y-4">
              <div>
                <label className="label">Voz</label>
                <select
                  value={voiceForm.voiceId}
                  onChange={(e) => {
                    const voice = availableVoices.find(v => v.voice_id === e.target.value);
                    setVoiceForm({
                      ...voiceForm,
                      voiceId: e.target.value,
                      voiceName: voice?.name || ''
                    });
                  }}
                  className="input-field"
                >
                  <option value="">Selecciona una voz</option>
                  {availableVoices.map(voice => (
                    <option key={voice.voice_id} value={voice.voice_id}>
                      {voice.name} {voice.category ? `(${voice.category})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Tu plan Creator tiene acceso a voces: premade, cloned y generated
                </p>
              </div>

              <div>
                <label className="label">Mensaje de Bienvenida</label>
                <textarea
                  value={voiceForm.welcomeMessage}
                  onChange={(e) => setVoiceForm({...voiceForm, welcomeMessage: e.target.value})}
                  className="input-field"
                  rows="4"
                  placeholder="Bienvenido {name} de {company} a nuestro evento..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  Usa {'{name}'} para el nombre y {'{company}'} para la empresa del asistente
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={voiceForm.isActive}
                    onChange={(e) => setVoiceForm({...voiceForm, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  Activar mensajes de voz
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Guardando...' : 'Guardar Configuración'}
                </button>
                <button
                  type="button"
                  onClick={handleTestVoice}
                  disabled={loading || !voiceForm.voiceId}
                  className="btn-secondary"
                >
                  Probar Voz
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'messaging' && (
          <MessagingTab
            messagingForm={messagingForm}
            setMessagingForm={setMessagingForm}
            loading={loading}
            emailHeaderFile={emailHeaderFile}
            emailSignatureFile={emailSignatureFile}
            handleSaveMessaging={handleSaveMessaging}
            handleEmailHeaderFileChange={handleEmailHeaderFileChange}
            handleEmailSignatureFileChange={handleEmailSignatureFileChange}
          />
        )}

        {activeTab === 'calls' && (
          <CallsTab
            callsForm={callsForm}
            setCallsForm={setCallsForm}
            callHistory={callHistory}
            availableCallVoices={availableCallVoices}
            voiceSearchTerm={voiceSearchTerm}
            setVoiceSearchTerm={setVoiceSearchTerm}
            showVoiceModal={showVoiceModal}
            setShowVoiceModal={setShowVoiceModal}
            loading={loading}
            loadingCalls={loadingCalls}
            globalTwilioNumber={globalTwilioNumber}
            attendees={attendees}
            handleSaveCalls={handleSaveCalls}
            handleConfigureTwilioWebhook={handleConfigureTwilioWebhook}
            handleConfigureSIPAuth={handleConfigureSIPAuth}
            handleStartCallCampaign={handleStartCallCampaign}
            handleRunDiagnostic={handleRunDiagnostic}
          />
        )}

        {activeTab === 'attendees' && (
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Asistentes ({attendees.length})</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => { loadAttendees(); showMessage('success', 'Lista actualizada'); }}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Actualizar lista de asistentes"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Actualizar</span>
                </button>
              {attendees.length > 0 && (
                <>
                  <button
                    onClick={handleSendReminder}
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Enviar recordatorio a todos los asistentes"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Recordatorio</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Exportar asistentes a PDF"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>PDF</span>
                  </button>
                  <button
                    onClick={handleExportExcel}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Exportar asistentes a Excel"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Excel</span>
                  </button>
                  <button
                    onClick={handleClearAllAttendees}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Limpiar todos los registros para pruebas"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>{loading ? 'Limpiando...' : 'Limpiar Datos'}</span>
                  </button>
                </>
              )}
              </div>
            </div>

            {attendees.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="mt-4 text-gray-500">No hay asistentes registrados todavía</p>
                <p className="mt-2 text-sm text-gray-400">Comparte el formulario de registro para empezar a recibir inscripciones</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendees.map((attendee) => (
                      <tr key={attendee.id}>
                        <td className="px-6 py-4 whitespace-nowrap">{attendee.fullName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{attendee.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{attendee.company}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded ${attendee.checkedIn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {attendee.checkedIn ? 'Sí' : 'No'}
                          </span>
                        </td>
<td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Botón Eliminar - siempre visible */}
                            <button
                              onClick={() => handleDeleteAttendee(attendee.id)}
                              className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition"
                              title="Eliminar asistente"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0016.138 21H7.862a2 2 0 001-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1v3M4 7h16" />
                              </svg>
                              Eliminar
                            </button>

                            {/* Botón Ver ficha - solo si NO ha hecho check-in */}
                            {!Boolean(attendee.checkedIn) && (
                              <button
                                onClick={() => handleShowEntryPass(attendee)}
                                className="inline-flex items-center px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition"
                                title="Ver ficha de ingreso"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Ver ficha
                              </button>
                            )}

                            {/* Botón Ver foto facial - si tiene reconocimiento facial o foto para mosaico */}
                            {(attendee.hasFacialRecognition || attendee.photoUrl) && (
                              <button
                                onClick={() => handleShowPhoto(attendee)}
                                className="inline-flex items-center px-2 py-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition"
                                title="Ver foto facial"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 8a3 3 0 11-6 0 3 3 0 016 0zM5.303 5.303L3 3m10.597 10.597l2.121 2.121M9 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                Ver foto
                              </button>
                            )}

                            {/* Botón Borrar foto/facial - si tiene reconocimiento facial o foto */}
                            {(attendee.hasFacialRecognition || attendee.photoUrl) && (
                              <button
                                onClick={() => handleDeletePhoto(attendee.id)}
                                className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition"
                                title="Eliminar foto facial"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Borrar foto
                              </button>
                            )}

                            {/* Botón Borrar check-in - solo si ha hecho check-in */}
                            {Boolean(attendee.checkedIn) && (
                              <button
                                onClick={() => handleClearCheckIn(attendee.id)}
                                className="inline-flex items-center px-2 py-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded transition"
                                title="Borrar check-in para pruebas"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Borrar check-in
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {attendees.length > 0 && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Modo de prueba</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>El botón "Limpiar Datos de Prueba" te permite eliminar todos los registros de este proyecto.</p>
                      <p className="mt-1">Útil para probar el formulario antes de lanzar el evento real.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'urls' && (
          <div className="card">
            <h2 className="text-xl font-bold mb-4">URLs del Proyecto</h2>
            <div className="space-y-4">
              {/* Formulario de Registro */}
              <div>
                <label className="label">Formulario de Registro</label>
                <div className="flex items-center space-x-2">
                  <input type="text" className="input-field flex-1" value={`${baseUrl}/form/${project.slug}`} readOnly />
                  <button
                    type="button"
                    onClick={() => window.open(`${baseUrl}/form/${project.slug}`, '_blank')}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                    title="Abrir en nueva pestaña"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShowQR('Formulario de Registro', `${baseUrl}/form/${project.slug}`)}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                    title="Ver QR"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(`${baseUrl}/form/${project.slug}`)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    title="Copiar URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareUrl('Formulario de Registro', `${baseUrl}/form/${project.slug}`)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                    title="Compartir URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Control de Acceso */}
              <div>
                <label className="label">Control de Acceso</label>
                <div className="flex items-center space-x-2">
                  <input type="text" className="input-field flex-1" value={`${baseUrl}/access/${project.id}`} readOnly />
                  <button
                    type="button"
                    onClick={() => window.open(`${baseUrl}/access/${project.id}`, '_blank')}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                    title="Abrir en nueva pestaña"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShowQR('Control de Acceso', `${baseUrl}/access/${project.id}`)}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                    title="Ver QR"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(`${baseUrl}/access/${project.id}`)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    title="Copiar URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareUrl('Control de Acceso', `${baseUrl}/access/${project.id}`)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                    title="Compartir URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Mosaico de Fotos */}
              <div>
                <label className="label">Mosaico de Fotos</label>
                <div className="flex items-center space-x-2">
                  <input type="text" className="input-field flex-1" value={`${baseUrl}/mosaic/${project.id}`} readOnly />
                  <button
                    type="button"
                    onClick={() => window.open(`${baseUrl}/mosaic/${project.id}`, '_blank')}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                    title="Abrir en nueva pestaña"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShowQR('Mosaico de Fotos', `${baseUrl}/mosaic/${project.id}`)}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                    title="Ver QR"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(`${baseUrl}/mosaic/${project.id}`)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    title="Copiar URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareUrl('Mosaico de Fotos', `${baseUrl}/mosaic/${project.id}`)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                    title="Compartir URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Panel Fotografico */}
              <div>
                <label className="label">Panel Fotografico (Subir Fotos)</label>
                <div className="flex items-center space-x-2">
                  <input type="text" className="input-field flex-1" value={photoboothToken ? `${baseUrl}/photobooth/${project.slug}/panel/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}`} readOnly />
                  <button
                    type="button"
                    onClick={() => window.open(photoboothToken ? `${baseUrl}/photobooth/${project.slug}/panel/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}`, '_blank')}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                    title="Abrir en nueva pestaña"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShowQR('Panel Fotografico', photoboothToken ? `${baseUrl}/photobooth/${project.slug}/panel/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}`)}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                    title="Ver QR"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(photoboothToken ? `${baseUrl}/photobooth/${project.slug}/panel/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}`)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    title="Copiar URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareUrl('Panel Fotografico', photoboothToken ? `${baseUrl}/photobooth/${project.slug}/panel/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}`)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                    title="Compartir URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
                {photoboothToken && (
                  <p className="text-xs text-gray-500 mt-1">Token valido por 24 horas</p>
                )}
              </div>

              {/* Mural PhotoBooth */}
              <div>
                <label className="label">Mural PhotoBooth</label>
                <div className="flex items-center space-x-2">
                  <input type="text" className="input-field flex-1" value={photoboothToken ? `${baseUrl}/photobooth/${project.slug}/mural/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/mural`} readOnly />
                  <button
                    type="button"
                    onClick={() => window.open(photoboothToken ? `${baseUrl}/photobooth/${project.slug}/mural/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/mural`, '_blank')}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                    title="Abrir en nueva pestaña"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShowQR('Mural PhotoBooth', photoboothToken ? `${baseUrl}/photobooth/${project.slug}/mural/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/mural`)}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                    title="Ver QR"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(photoboothToken ? `${baseUrl}/photobooth/${project.slug}/mural/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/mural`)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    title="Copiar URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareUrl('Mural PhotoBooth', photoboothToken ? `${baseUrl}/photobooth/${project.slug}/mural/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/mural`)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                    title="Compartir URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
                {photoboothToken && (
                  <p className="text-xs text-gray-500 mt-1">Token valido por 24 horas</p>
                )}
              </div>

              {/* Pantalla de Proyeccion */}
              <div>
                <label className="label">Pantalla de Proyeccion (TV)</label>
                <div className="flex items-center space-x-2">
                  <input type="text" className="input-field flex-1" value={photoboothToken ? `${baseUrl}/photobooth/${project.slug}/tv/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/tv`} readOnly />
                  <button
                    type="button"
                    onClick={() => window.open(photoboothToken ? `${baseUrl}/photobooth/${project.slug}/tv/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/tv`, '_blank')}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition"
                    title="Abrir en nueva pestaña"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShowQR('Pantalla de Proyeccion', photoboothToken ? `${baseUrl}/photobooth/${project.slug}/tv/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/tv`)}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition"
                    title="Ver QR"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(photoboothToken ? `${baseUrl}/photobooth/${project.slug}/tv/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/tv`)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                    title="Copiar URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareUrl('Pantalla de Proyeccion', photoboothToken ? `${baseUrl}/photobooth/${project.slug}/tv/${photoboothToken}` : `${baseUrl}/photobooth/${project.slug}/tv`)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition"
                    title="Compartir URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
                {photoboothToken && (
                  <p className="text-xs text-gray-500 mt-1">Token valido por 24 horas</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <PhotosTab
            photoBoothPhotos={photoBoothPhotos}
            bannedPhotos={bannedPhotos}
            loadingPhotos={loadingPhotos}
            loadPhotoBoothPhotos={loadPhotoBoothPhotos}
            loadBannedPhotos={loadBannedPhotos}
            handleBanPhoto={handleBanPhoto}
            handleUnbanPhoto={handleUnbanPhoto}
            handleDeletePhotoPermanently={handleDeleteBoothPhoto}
          />
        )}

        {activeTab === 'mural' && (
          <MuralAdmin project={project} />
        )}

        {activeTab === 'forms' && (
          <CustomFormsTab 
            projectId={id} 
            projectSlug={project?.slug} 
          />
        )}

        {activeTab === 'documents' && (
          <DocumentEditorTab 
            projectId={id} 
            projectSlug={project?.slug} 
          />
        )}
      </div>

      <EntryPassModal
        show={showEntryPass}
        attendee={selectedAttendee}
        project={project}
        onClose={() => {
          setShowEntryPass(false);
          setSelectedAttendee(null);
        }}
      />

      {/* Modal para mostrar foto facial */}
      <PhotoModal
        show={showPhotoModal}
        attendee={selectedAttendee}
        onClose={() => {
          setShowPhotoModal(false);
          setSelectedAttendee(null);
        }}
      />

      {/* Modal de QR */}
      {showQRModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold">Codigo QR - {qrModalData.title}</h2>
              <button
                type="button"
                onClick={() => setShowQRModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-6 text-center">
              <div className="bg-white p-4 rounded-lg inline-block mb-4 border">
                <QRCodeSVG value={qrModalData.url} size={200} level="H" />
              </div>
              <p className="text-sm text-gray-600 mb-4 break-all">{qrModalData.url}</p>
              <div className="flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleCopyUrl(qrModalData.url);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  Copiar URL
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleShareUrl(qrModalData.title, qrModalData.url);
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                >
                  Compartir
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={() => setShowQRModal(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProjectDetail;
